import { useCart } from "@/contexts/CartContext";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Trash2, MinusCircle, PlusCircle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "@/utils/currencyUtils";

const CartSheet = () => {
  const { items, removeFromCart, updateQuantity } = useCart();
  const navigate = useNavigate();

  const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  if (!items.length) {
    return (
      <div className="flex h-full flex-col space-y-4">
        <SheetHeader>
          <SheetTitle>Shopping Cart</SheetTitle>
        </SheetHeader>
        <div className="flex flex-1 flex-col items-center justify-center space-y-4">
          <p className="text-muted-foreground">Your cart is empty</p>
          <Button onClick={() => navigate('/shop')}>Continue Shopping</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <SheetHeader>
        <SheetTitle>Shopping Cart ({items.length})</SheetTitle>
      </SheetHeader>
      <ScrollArea className="flex-1 pr-4">
        {items.map((item) => (
          <div key={item.id} className="space-y-3 py-4">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <h4 className="font-medium">{item.name}</h4>
                <p className="text-sm text-muted-foreground">
                  {formatCurrency(item.price, "GHS")}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeFromCart(item.product_id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => updateQuantity(item.product_id, Math.max(1, item.quantity - 1))}
              >
                <MinusCircle className="h-4 w-4" />
              </Button>
              <span className="w-8 text-center">{item.quantity}</span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
              >
                <PlusCircle className="h-4 w-4" />
              </Button>
              <span className="ml-auto font-medium">
                {formatCurrency(item.price * item.quantity, "GHS")}
              </span>
            </div>
            <Separator />
          </div>
        ))}
      </ScrollArea>
      <div className="space-y-4 pt-4">
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="font-medium">Total</span>
            <span className="font-medium">{formatCurrency(total, "GHS")}</span>
          </div>
        </div>
        <Button 
          className="w-full" 
          size="lg"
          onClick={() => navigate('/cart')}
        >
          Proceed to Checkout
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default CartSheet;